import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import { numberLocale } from '@/lib/language';
import * as Sentry from '@sentry/react';
import { useEnforcement } from '@/hooks/useEnforcement';
import { useSubscription } from '@/hooks/useSubscription';
import { handleUpgrade, getCurrency } from '@/lib/checkout';
import {
  defaultBillingCycle,
  formatPlanPrice,
  formatProductsPhrase,
  isCapTier,
  lookupForCycle,
  planDisplayName,
  planTierLabel,
} from '@/lib/plans';
import { trackConversion } from '@/lib/analytics';
import { Button } from '@/components/ui/button';
import { ArrowUpRight, ChevronRight, Sparkles, X } from 'lucide-react';

export const PostFetchRecommendation: React.FC = () => {
  const { t, i18n } = useTranslation('enforcement');
  const { totalProducts, limit, plan, recommendedPlan } = useEnforcement();
  const { subscription } = useSubscription();
  const [dismissed, setDismissed] = useState(false);
  const [loading, setLoading] = useState(false);

  const isVisible = !dismissed && !!recommendedPlan && totalProducts > limit;

  // Funnel-Event (Bericht 8.3 Punkt 6): einmal pro Mount, und erst wenn die
  // Empfehlung tatsaechlich sichtbar wird (Enforcement-Daten laden nach —
  // beim ersten Render ist totalProducts noch 0). Ref-Guard nach dem Muster
  // von FeedCheckInsights gegen Re-Fires durch Re-Render.
  const shownTracked = useRef(false);
  useEffect(() => {
    if (isVisible && !shownTracked.current) {
      shownTracked.current = true;
      trackConversion('post_fetch_recommendation_shown', {
        plan,
        target: recommendedPlan?.tier ?? '',
      });
    }
  }, [isVisible, plan, recommendedPlan?.tier]);

  if (!isVisible || !recommendedPlan) return null;

  const currency = getCurrency();
  const isBand = isCapTier(recommendedPlan.tier);
  // CRO 21.08.2026: Neuabschluesse checken mit Jahresabrechnung aus,
  // Bestandskunden behalten ihr Intervall. Nur fuer Baender (Legacy-Tarife
  // sind nicht mehr kaufbar; getRecommendedPlan liefert ohnehin nur Baender).
  const cycle = isBand ? defaultBillingCycle(subscription) : 'monthly';
  const price = formatPlanPrice(recommendedPlan, currency, cycle);
  const numLocale = numberLocale(i18n.language);
  // Band-Tiers wuerden kapitalisiert "Cap1000" heissen — der Label kommt
  // deshalb aus PLANS_BY_TIER (Baender lokalisiert, Legacy-Eigennamen roh).
  const planLabel = planTierLabel(plan, i18n?.language, t);
  const recommendedName = planDisplayName(recommendedPlan, i18n?.language, t);

  const onUpgrade = async () => {
    trackConversion('post_fetch_recommendation_clicked', { plan, target: recommendedPlan.tier });
    setLoading(true);
    try {
      const res = await handleUpgrade(lookupForCycle(recommendedPlan, cycle), currency);
      // Abgebrochene B2B-Bestaetigung (oder kein Redirect): Spinner loesen.
      if (!res.redirected) setLoading(false);
    } catch (error) {
      Sentry.captureException(error, { tags: { feature: 'billing', action: 'upgrade-from-recommendation' } });
      setLoading(false);
    }
  };

  return (
    <div className="rounded-lg border border-primary/20 bg-primary/5 p-4 space-y-3 mt-4">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-primary flex-shrink-0" />
          <p className="text-sm font-medium text-primary">
            {t('postFetch.recommendation')}
          </p>
        </div>
        <Button
          size="icon"
          variant="ghost"
          className="h-6 w-6 -mt-1 -mr-1"
          onClick={() => setDismissed(true)}
          aria-label={t('postFetch.dismiss')}
        >
          <X className="h-3.5 w-3.5" />
        </Button>
      </div>

      <p className="text-sm text-muted-foreground">
        {/* Band: Name == Kapazitaets-Phrase, und "dein 500 Produkte-Tarif"
            waere kein Deutsch — die Band-Variante nennt den aktuellen Tarif
            nicht beim Namen und wiederholt die Kapazitaet nicht. */}
        {isBand ? (
          <Trans
            i18nKey="postFetch.descriptionBand"
            ns="enforcement"
            values={{
              total: totalProducts.toLocaleString(numLocale),
              limit: limit.toLocaleString(numLocale),
              recommended: recommendedName,
            }}
            components={{ strong: <span className="font-semibold text-foreground" /> }}
          />
        ) : (
          <Trans
            i18nKey="postFetch.description"
            ns="enforcement"
            values={{
              total: totalProducts.toLocaleString(numLocale),
              plan: planLabel,
              limit: limit.toLocaleString(numLocale),
              recommended: recommendedPlan.name,
              productsLabel: formatProductsPhrase(recommendedPlan, i18n?.language, t),
            }}
            components={{ strong: <span className="font-semibold text-foreground" /> }}
          />
        )}
      </p>

      <div className="flex items-center gap-2">
        <Button size="sm" onClick={onUpgrade} disabled={loading} className="gap-1">
          <ArrowUpRight className="h-3.5 w-3.5" />
          {/* Jahres-Zweig sagt /Jahr — der Betrag ist der volle Jahresbetrag. */}
          {t(cycle === 'yearly' ? 'postFetch.upgradeButtonYearly' : 'postFetch.upgradeButton', {
            plan: recommendedName,
            price,
          })}
        </Button>
        <Link
          to="/pricing"
          className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-primary transition-colors"
        >
          {t('postFetch.comparePlans')}
          <ChevronRight className="h-3 w-3" />
        </Link>
      </div>
    </div>
  );
};
